import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    persisted: { type: Boolean, default: false },
    statesUrl: { type: String, default: "/api/states" },
    citiesUrl: { type: String, default: "/api/states/cities" },
    noStatesMessage: { type: String, default: "No states available" },
    noCitiesMessage: { type: String, default: "No cities available" }
  }

  static classes = ["hidden", "loading"]

  static targets = [
    "locationSelector",
    "displayLocation",
    "countrySelect",
    "stateSelect",
    "stateContainer",
    "citySelect",
    "cityContainer",
    "locationText",
    "selectorButton"
  ]

  async connect() {
    this.validateRequiredTargets()
    this.setupCountryChangeListener()
    this.setupStateChangeListener()

    if (this.countrySelectTarget.value) {
      await this.loadStates(this.countrySelectTarget.value)
      this.showStateSelection()

      // Check if there's a saved state
      const hasSavedState = this.hasStateSelectTarget && this.stateSelectTarget.dataset.selected

      if (hasSavedState) {
        const savedState = this.stateSelectTarget.dataset.selected
        const option = Array.from(this.stateSelectTarget.options).find(
          (opt) => opt.value === savedState
        )
        if (option) {
          option.selected = true
        }

        if (this.stateSelectTarget.tomselect) {
          this.stateSelectTarget.tomselect.setValue(savedState)
        }

        // Load cities for the specific state
        await this.loadCities(this.countrySelectTarget.value, savedState)
      } else {
        // No saved state, load all cities for the country
        await this.loadCitiesByCountry(this.countrySelectTarget.value)
      }

      this.showCitySelection()

      // Handle saved city selection (works for both state-specific and country-wide cities)
      if (this.hasCitySelectTarget && this.citySelectTarget.dataset.selected) {
        const savedCity = this.citySelectTarget.dataset.selected
        const cityOption = Array.from(this.citySelectTarget.options).find(
          (opt) => opt.value === savedCity
        )
        if (cityOption) {
          cityOption.selected = true
        }

        if (this.citySelectTarget.tomselect) {
          this.citySelectTarget.tomselect.setValue(savedCity)
        }
      }
    }
  }

  // Helper methods to dispatch flash events
  dispatchFlashError(message, context = 'Location Selection') {
    const event = new CustomEvent('flash:error', {
      detail: { message, context },
      bubbles: true
    })
    this.element.dispatchEvent(event)
  }

  dispatchFlashWarning(message) {
    const event = new CustomEvent('flash:warning', {
      detail: { message },
      bubbles: true
    })
    this.element.dispatchEvent(event)
  }

  setupCountryChangeListener() {
    if (this.hasCountrySelectTarget) {
      this.countrySelectTarget.addEventListener('change', this.handleCountryChange.bind(this))

      this.element.addEventListener('tom-select:change', (event) => {
        if (event.target === this.countrySelectTarget) {
          this.handleCountryChange(event)
        }
      })
    }
  }

  setupStateChangeListener() {
    if (this.hasStateSelectTarget) {
      this.stateSelectTarget.addEventListener('change', this.handleStateChange.bind(this))

      this.element.addEventListener('tom-select:change', (event) => {
        if (event.target === this.stateSelectTarget) {
          this.handleStateChange(event)
        }
      })
    }
  }

  async handleCountryChange(event) {
    const countryCode = event.target.value

    if (!countryCode) {
      this.hideStateSelection()
      this.hideCitySelection()
      return
    }

    try {
      // Clear previous selections
      this.clearStateSelection()
      this.clearCitySelection()

      await this.loadStates(countryCode)
      this.showStateSelection()

      await this.loadCitiesByCountry(countryCode)
      this.showCitySelection()
    } catch (error) {
      this.dispatchFlashError(`Failed to load location data: ${error.message}`)
    }
  }

  async handleStateChange(event) {
    const stateCode = event.target.value
    const countryCode = this.countrySelectTarget.value

    if (!countryCode) {
      this.hideCitySelection()
      return
    }

    try {
      // Always clear cities first when state changes
      this.clearCitySelection()

      if (!stateCode) {
        // If no state selected, load all cities for the country
        await this.loadCitiesByCountry(countryCode)
      } else {
        // If state selected, load cities for that specific state
        await this.loadCities(countryCode, stateCode)
      }

      this.showCitySelection()
    } catch (error) {
      this.dispatchFlashError(`Failed to load cities: ${error.message}`)
    }
  }

  async loadStates(countryCode) {
    if (!this.hasStateSelectTarget) return

    try {
      this.showStateLoading()

      const response = await fetch(`${this.statesUrlValue}?country=${countryCode}`)

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const states = await response.json()
      this.populateStates(states)
      this.showStateSelection()

    } catch (error) {
      this.hideStateSelection()
      throw error
    } finally {
      this.hideStateLoading()
    }
  }

  async loadCities(countryCode, stateCode) {
    if (!this.hasCitySelectTarget) return

    try {
      this.showCityLoading()

      const response = await fetch(`${this.citiesUrlValue}?country=${countryCode}&state=${stateCode}`)

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const cities = await response.json()
      this.populateCities(cities)
      this.showCitySelection()
    } catch (error) {
      this.hideCitySelection()
      throw error
    } finally {
      this.hideCityLoading()
    }
  }

  async loadCitiesByCountry(countryCode) {
    if (!this.hasCitySelectTarget) return

    try {
      this.showCityLoading()

      const response = await fetch(`${this.citiesUrlValue}?country=${countryCode}`)

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const cities = await response.json()
      this.populateCities(cities)
      this.showCitySelection()
    } catch (error) {
      console.warn(`Failed to load cities for country ${countryCode}:`, error.message)
    } finally {
      this.hideCityLoading()
    }
  }

  populateStates(states) {
    if (!this.hasStateSelectTarget) return

    // If TomSelect is initialized, handle it properly
    if (this.stateSelectTarget.tomselect) {
      // Clear all options and search cache in TomSelect
      this.stateSelectTarget.tomselect.clearOptions()
      this.stateSelectTarget.tomselect.clear()

      // Add the default option
      this.stateSelectTarget.tomselect.addOption({ value: '', text: 'Select State' })

      // Add new states
      states.forEach(state => {
        const value = typeof state === 'string' ? state : state.code || state.name
        const text = typeof state === 'string' ? state : state.name
        this.stateSelectTarget.tomselect.addOption({ value, text })
      })

      // Refresh the dropdown
      this.stateSelectTarget.tomselect.refreshOptions(false)
    } else {
      // Fallback for regular select elements
      this.stateSelectTarget.innerHTML = '<option value="">Select State</option>'

      states.forEach(state => {
        const option = document.createElement('option')
        option.value = typeof state === 'string' ? state : state.code || state.name
        option.textContent = typeof state === 'string' ? state : state.name
        this.stateSelectTarget.appendChild(option)
      })
    }
  }

  populateCities(cities) {
    if (!this.hasCitySelectTarget) return

    // If TomSelect is initialized, we need to clear and recreate options properly
    if (this.citySelectTarget.tomselect) {
      // Clear all options and search cache in TomSelect
      this.citySelectTarget.tomselect.clearOptions()
      this.citySelectTarget.tomselect.clear()

      // Add the default option
      this.citySelectTarget.tomselect.addOption({ value: '', text: 'Select City' })

      // Add new cities
      cities.forEach(city => {
        const value = typeof city === 'string' ? city : city.code || city.name
        const text = typeof city === 'string' ? city : city.name
        this.citySelectTarget.tomselect.addOption({ value, text })
      })

      // Refresh the dropdown
      this.citySelectTarget.tomselect.refreshOptions(false)
    } else {
      // Fallback for regular select elements
      this.citySelectTarget.innerHTML = '<option value="">Select City</option>'

      cities.forEach(city => {
        const option = document.createElement('option')
        option.value = typeof city === 'string' ? city : city.code || city.name
        option.textContent = typeof city === 'string' ? city : city.name
        this.citySelectTarget.appendChild(option)
      })
    }
  }

  clearStateSelection() {
    if (this.hasStateSelectTarget) {
      this.stateSelectTarget.value = ''
      this.stateSelectTarget.selectedIndex = 0

      // Clear TomSelect if present
      if (this.stateSelectTarget.tomselect) {
        this.stateSelectTarget.tomselect.setValue('')
      }
    }
  }

  clearCitySelection() {
    if (this.hasCitySelectTarget) {
      this.citySelectTarget.value = ''
      this.citySelectTarget.selectedIndex = 0

      // Clear TomSelect if present
      if (this.citySelectTarget.tomselect) {
        this.citySelectTarget.tomselect.setValue('')
      }
    }
  }

  showStateSelection() {
    if (this.hasStateContainerTarget) {
      this.showElement(this.stateContainerTarget)
    }
  }

  hideStateSelection() {
    if (this.hasStateContainerTarget) {
      this.hideElement(this.stateContainerTarget)
    }

    this.clearStateSelection()
  }

  showCitySelection() {
    if (this.hasCityContainerTarget) {
      this.showElement(this.cityContainerTarget)
    }
  }

  hideCitySelection() {
    if (this.hasCityContainerTarget) {
      this.hideElement(this.cityContainerTarget)
    }

    this.clearCitySelection()
  }

  showStateLoading() {
    if (this.hasStateSelectTarget && this.hasLoadingClass) {
      this.stateSelectTarget.classList.add(this.loadingClass)
    }
  }

  hideStateLoading() {
    if (this.hasStateSelectTarget && this.hasLoadingClass) {
      this.stateSelectTarget.classList.remove(this.loadingClass)
    }
  }

  showCityLoading() {
    if (this.hasCitySelectTarget && this.hasLoadingClass) {
      this.citySelectTarget.classList.add(this.loadingClass)
    }
  }

  hideCityLoading() {
    if (this.hasCitySelectTarget && this.hasLoadingClass) {
      this.citySelectTarget.classList.remove(this.loadingClass)
    }
  }

  validateRequiredTargets() {
    const requiredTargets = [
      'locationSelector',
      'displayLocation',
      'countrySelect',
      'selectorButton'
    ]

    const missingTargets = requiredTargets.filter(target => {
      const hasTarget = this[`has${this.capitalize(target)}Target`]
      return !hasTarget
    })

    if (missingTargets.length > 0) {
      this.dispatchFlashError(
        `Missing required targets: ${missingTargets.join(', ')}`,
        'Controller Setup'
      )
      return false
    }

    return true
  }

  validateLocationSelection() {
    if (!this.hasCountrySelectTarget) {
      this.dispatchFlashError('Country selection not available', 'Form Validation')
      return null
    }

    const selectedCountryOption = this.countrySelectTarget.selectedOptions[0]

    if (!selectedCountryOption || !this.countrySelectTarget.value.trim()) {
      this.dispatchFlashError('Please select a country', 'Form Validation')
      return null
    }

    const countryValue = this.countrySelectTarget.value.trim()
    const countryText = selectedCountryOption.textContent?.trim()

    if (!countryValue || !countryText) {
      this.dispatchFlashError('Invalid country selection', 'Form Validation')
      return null
    }

    // State is optional
    let stateValue = null
    let stateText = null

    if (this.hasStateSelectTarget && this.stateSelectTarget.value) {
      const selectedStateOption = this.stateSelectTarget.selectedOptions[0]
      stateValue = this.stateSelectTarget.value.trim()
      stateText = selectedStateOption?.textContent?.trim() || stateValue
    }

    let cityValue = null
    let cityText = null

    if (this.hasCitySelectTarget && this.citySelectTarget.value) {
      const selectedCityOption = this.citySelectTarget.selectedOptions[0]
      cityValue = this.citySelectTarget.value.trim()
      cityText = selectedCityOption?.textContent?.trim() || cityValue
    }

    return {
      country: {
        value: countryValue,
        text: countryText
      },
      state: stateValue ? {
        value: stateValue,
        text: stateText
      } : null,
      city: cityValue ? {
        value: cityValue,
        text: cityText
      } : null
    }
  }

  toggleLocationSelector() {
    try {
      if (!this.hasLocationSelectorTarget) {
        this.dispatchFlashError('Location selector not available')
        return
      }

      this.showElement(this.locationSelectorTarget)

      if (this.hasCountrySelectTarget) {
        this.countrySelectTarget.focus()
      }
    } catch (error) {
      this.dispatchFlashError(`Error showing location selector: ${error.message}`)
    }
  }

  saveLocation() {
    try {
      const selectedLocation = this.validateLocationSelection()
      if (!selectedLocation) return

      this.updateDisplayContent(selectedLocation)

      this.hideElement(this.locationSelectorTarget)
      this.showElement(this.displayLocationTarget)
      this.hideElement(this.selectorButtonTarget)

      const locationParts = [selectedLocation.country.text]
      if (selectedLocation.state) locationParts.unshift(selectedLocation.state.text)
      if (selectedLocation.city) locationParts.unshift(selectedLocation.city.text)
    } catch (error) {
      this.dispatchFlashError(`Error saving location: ${error.message}`)
      this.handleSaveError()
    }
  }

  cancelLocation() {
    try {
      this.hideElement(this.locationSelectorTarget)

      if (this.persistedValue) {
        this.showElement(this.displayLocationTarget)
        this.hideElement(this.selectorButtonTarget)
      } else {
        this.hideElement(this.displayLocationTarget)
        this.showElement(this.selectorButtonTarget)

        if (this.hasCountrySelectTarget) {
          this.countrySelectTarget.value = ''
          if (this.countrySelectTarget.tomselect) {
            this.countrySelectTarget.tomselect.setValue('')
          }
        }
        this.hideStateSelection()
        this.hideCitySelection()
      }

      this.dispatch('locationCancelled')

    } catch (error) {
      this.dispatchFlashError(`Error cancelling location selection: ${error.message}`)
    }
  }

  editLocation() {
    try {
      if (!this.hasLocationSelectorTarget) {
        this.dispatchFlashError('Location selector not available')
        return
      }

      this.showElement(this.locationSelectorTarget)
      this.hideElement(this.displayLocationTarget)
      this.hideElement(this.selectorButtonTarget)

      if (this.hasCountrySelectTarget) {
        this.countrySelectTarget.focus()
      }

      this.dispatch('locationEditing')

    } catch (error) {
      this.dispatchFlashError(`Error editing location: ${error.message}`)
    }
  }

  resetLocation() {
    try {
      if (this.hasCountrySelectTarget) {
        this.countrySelectTarget.selectedIndex = 0
        if (this.countrySelectTarget.tomselect) {
          this.countrySelectTarget.tomselect.setValue('')
        }
      }

      this.hideStateSelection()
      this.hideCitySelection()

      if (this.hasLocationTextTarget) {
        this.locationTextTarget.textContent = ''
      }

      this.hideElement(this.displayLocationTarget)
      this.hideElement(this.locationSelectorTarget)
      this.showElement(this.selectorButtonTarget)

      this.dispatch('locationReset')
    } catch (error) {
      this.dispatchFlashError(`Error resetting location: ${error.message}`)
    }
  }

  updateDisplayContent(location) {
    if (!location?.country) {
      throw new Error('Country information is required')
    }

    const locationParts = [location.country.text]
    if (location.state) locationParts.unshift(location.state.text)
    if (location.city) locationParts.unshift(location.city.text)

    const displayText = locationParts.join(', ')

    if (this.hasLocationTextTarget) {
      this.locationTextTarget.textContent = displayText

      this.locationTextTarget.dataset.countryValue = location.country.value
      this.locationTextTarget.dataset.countryText = location.country.text

      if (location.state) {
        this.locationTextTarget.dataset.stateValue = location.state.value
        this.locationTextTarget.dataset.stateText = location.state.text
      } else {
        delete this.locationTextTarget.dataset.stateValue
        delete this.locationTextTarget.dataset.stateText
      }

      if (location.city) {
        this.locationTextTarget.dataset.cityValue = location.city.value
        this.locationTextTarget.dataset.cityText = location.city.text
      } else {
        delete this.locationTextTarget.dataset.cityValue
        delete this.locationTextTarget.dataset.cityText
      }
    } else {
      this.dispatchFlashWarning('Location text target not found - text not updated')
    }
  }

  handleSaveError() {
    try {
      this.cancelLocation()
    } catch (fallbackError) {
      this.dispatchFlashError('Critical error in location handling', 'System Error')
    }
  }

  getCurrentLocation() {
    if (this.hasLocationTextTarget && this.locationTextTarget.textContent) {
      const data = this.locationTextTarget.dataset

      return {
        country: {
          value: data.countryValue || null,
          text: data.countryText || null
        },
        state: data.stateValue ? {
          value: data.stateValue,
          text: data.stateText
        } : null,
        city: data.cityValue ? {
          value: data.cityValue,
          text: data.cityText
        } : null,
        displayText: this.locationTextTarget.textContent
      }
    }
    return null
  }

  setLocation(countryValue, countryText, stateValue = null, stateText = null, cityValue = null, cityText = null) {
    try {
      const location = {
        country: { value: countryValue, text: countryText },
        state: stateValue ? { value: stateValue, text: stateText } : null,
        city: cityValue ? { value: cityValue, text: cityText } : null
      }

      this.updateDisplayContent(location)
      this.showElement(this.displayLocationTarget)
      this.hideElement(this.selectorButtonTarget)
      this.hideElement(this.locationSelectorTarget)

      const locationParts = [location.country.text]
      if (location.state) locationParts.unshift(location.state.text)
      if (location.city) locationParts.unshift(location.city.text)
    } catch (error) {
      this.dispatchFlashError(`Error setting location: ${error.message}`)
    }
  }

  showElement(element) {
    if (!element) {
      throw new Error('Element not found')
    }

    if (this.hasHiddenClass) {
      element.classList.remove(this.hiddenClass)
    } else {
      element.classList.remove('hidden')
    }
  }

  hideElement(element) {
    if (!element) {
      throw new Error('Element not found')
    }

    if (this.hasHiddenClass) {
      element.classList.add(this.hiddenClass)
    } else {
      element.classList.add('hidden')
    }
  }

  capitalize(string) {
    return string.charAt(0).toUpperCase() + string.slice(1)
  }

  disconnect() {
    if (this.hasCountrySelectTarget) {
      this.countrySelectTarget.removeEventListener('change', this.handleCountryChange.bind(this))
    }
    if (this.hasStateSelectTarget) {
      this.stateSelectTarget.removeEventListener('change', this.handleStateChange.bind(this))
    }
  }
}
